// UniversalComponent.jsx
'use strict';
import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import ChunkLoading from '../component/ChunkLoading.jsx';
import GeneralErrorToastr from '../component/GeneralErrorToastr.jsx';
import withRouter from '../component/WithRouter.jsx';
import { component as componentDebug } from '../resource/debug.js';

const log = componentDebug.extend('log:UniversalComponent');
const errorLog = componentDebug.extend('error:UniversalComponent');

const isServer = typeof window === 'undefined';
const Anchor = props => (
  <div {...props} style={{ width: '100%', height: '100%' }} />
);

class ErrorBoundary extends React.PureComponent {
  state = {
    error: null,
  };

  componentDidCatch(error) {
    this.setState({ error });
  }

  render() {
    const { error } = this.state;
    if (error) {
      return this.props.renderError({
        errorObject: error,
        errorBoundaryInstance: this,
      });
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  renderError: PropTypes.func.isRequired,
  children: PropTypes.node,
};

class UniversalComponent extends React.PureComponent {
  static components = {};
  nextTick = null;

  render() {
    const {
      filename,
      loading,
      error: errorElement,
      staticContext,
      importer,
      ...otherProps
    } = this.props;

    const anchorName = `UniversalAnchor_${filename}`;
    const loadingElement = <Anchor className={anchorName}>{loading}</Anchor>;

    let Component = UniversalComponent.components[filename];

    if (!Component) {
      Component = loadable(importer, {
        fallback: loadingElement,
      });

      UniversalComponent.components[filename] = Component;

      if (isServer) {
        Component.preload();
        // lodable component needs two rounds to construct
        staticContext.hasLoadingComponent = 2;
      }
    }

    log('render', filename, {
      ...this.props,
      isComponentExist: Boolean(Component),
    });

    return (
      <Anchor className={anchorName}>
        <ErrorBoundary
          renderError={({ errorObject, errorBoundaryInstance }) => {
            errorLog(
              'renderError',
              filename,
              errorObject,
              errorBoundaryInstance
            );
            return cloneElement(errorElement, {
              errorObject,
              retry: () => {
                errorBoundaryInstance.setState({
                  error: null,
                });
              },
            });
          }}
        >
          <Component {...otherProps} />
        </ErrorBoundary>
      </Anchor>
    );
  }
}

UniversalComponent.propTypes = {
  staticContext: PropTypes.object,
  filename: PropTypes.string.isRequired,
  loading: PropTypes.node,
  error: PropTypes.node,
  importer: PropTypes.func.isRequired,
};

UniversalComponent.defaultProps = {
  staticContext: {},
  loading: <ChunkLoading />,
  error: <GeneralErrorToastr />,
};

export default withRouter(UniversalComponent);
